import { EntityCustomViewParams, useDataSource } from "@firecms/core";
import { Alert, Chip, Typography } from "@firecms/ui";
import { useEffect, useState } from "react";

import { AppleGrantStatusEntity, GrantStateEnum } from "./apple_grant_status.types";
import {
  APPLE_INSIGHTS_STATUS_PATH,
  AppleInsightsStatusEntity,
  APPOINTMENT_METRIC_NAME,
  formatInsightsTimestamp,
  formatMetricValue,
  hasFigures,
  type InsightsEnvBucket,
  insightsStatusOf,
  isShowingStaleFigures,
  metricLabel,
  productionInsights,
  secondaryMetrics,
  unknownMetricNames,
} from "./apple_insights.types";

/**
 * # APPLE INSIGHTS — the read-only engagement panel
 *
 * The one surface that answers "is any of this working?". Apple's metric list includes
 * `PLACECARD_SCHEDULE_APPOINTMENT` — Apple counting taps on the booking button that every other part
 * of the ABC integration exists to publish — so that number is the hero tile and everything else is
 * context for it.
 *
 * ## ⚠️ IT READS A FIRESTORE PROJECTION. THE "LOAD REPORT" BUTTON IS GONE, AND SO IS THE REQUEST PATH
 *
 * This panel used to have a button that POSTed to a backend route, and copy on screen explaining that
 * the route was "not registered yet". Both are WITHDRAWN — recorded here rather than quietly removed,
 * because a reader who remembers that design should be able to see why it changed:
 *
 *  - a browser-callable route was never possible. `/internal/*` is guarded by `x-internal-secret`,
 *    which cannot ship in a bundle any user can read, and the same guard fronts `POST
 *    /internal/locations`, `/brands`, `/media` and `/showcases` — so shipping it to get ONE READ would
 *    expose every internal write endpoint;
 *  - a backend scheduled job now reads Apple daily and writes a secret-free projection to
 *    `apple_insights_status`, which this panel reads with the Firebase ID token the CMS already holds.
 *    Same shape as `apple_grant_status`, and the same reason: keep the credentialed surface server-side.
 *
 * So there is nothing left to request, and nothing to press. Consequences that the copy below states
 * out loud rather than leaving a reader to infer:
 *
 *  - **Not a writer, at all.** No Firestore write, no Apple call, no button. The document is read-only
 *    to this console.
 *  - **Daily, not live.** The figures move once a day. The panel prints WHEN they were observed, so a
 *    number that is four days old cannot be mistaken for this morning's.
 *  - **Empty for four different reasons, and it says which.** "Apple has nothing for this client yet"
 *    (the expected state at launch) is not "our job could not read Apple", which is not "Apple sent a
 *    report we could not read", which is not "Apple is still calculating".
 *  - **Nothing to show without an ACTIVE grant.** Without one there is no delegated access to read
 *    with, and saying that is more useful than an empty panel.
 */
export function AppleInsightsView({
  entity,
}: EntityCustomViewParams<Partial<AppleGrantStatusEntity>>) {
  const dataSource = useDataSource();
  const accountId = entity?.values.accountId ?? entity?.id;

  // Three-state, deliberately: "still loading" and "read failed" and "no document" are different
  // things, and a single `undefined` would collapse them into one silent blank panel.
  const [projection, setProjection] = useState<Partial<AppleInsightsStatusEntity> | null>(null);
  const [loading, setLoading] = useState(true);
  const [readError, setReadError] = useState<string | null>(null);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    setLoading(true);
    setReadError(null);
    // ⚠️ A PLAIN READ OF ONE DOCUMENT BY ID, through the FireCMS data source — never
    // `firebase/firestore` directly, which nothing in this repo imports. `apple_insights_status` is
    // not a registered FireCMS collection and does not need to be: `fetchEntity` resolves a path and
    // an id, and the panel wants exactly one document keyed by `accountId`.
    dataSource
      .fetchEntity<Partial<AppleInsightsStatusEntity>>({
        path: APPLE_INSIGHTS_STATUS_PATH,
        entityId: accountId,
      })
      .then((found) => {
        if (cancelled) return;
        setProjection(found?.values ?? null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setReadError(e instanceof Error ? e.message : "Could not read Apple Insights.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataSource, accountId]);

  // The grant is what carries the Apple company and the delegated token. Without an ACTIVE one there
  // is nothing to ask Apple about, and saying that is far more useful than an empty panel.
  const grantActive = entity?.values.state === GrantStateEnum.ACTIVE;
  const needsReview = entity?.values.needsReview === true;

  if (!accountId) {
    return (
      <div className="p-8">
        <Alert color="info">
          This record has no DearDoc account id, so there is no client to show metrics for.
        </Alert>
      </div>
    );
  }

  const bucket = productionInsights(projection);

  return (
    <div className="flex flex-col gap-6 p-8">
      <div className="flex flex-col gap-2">
        <Typography variant="h5">Apple Insights</Typography>
        <Typography variant="body2" color="secondary">
          Engagement metrics Apple reports for this client&rsquo;s place cards. Read-only, and
          refreshed by a backend job ONCE A DAY — nothing on this panel calls Apple, and nothing on
          it changes the client&rsquo;s Apple presence. The date beside the numbers is when they
          were read, not now.
        </Typography>
      </div>

      {!grantActive && (
        <Alert color="warning">
          This client&rsquo;s Apple link is not active, so Apple has no delegated access for us to
          read metrics with. Re-link the client before expecting numbers here.
        </Alert>
      )}

      {needsReview && (
        <Alert color="warning">
          This account is flagged for review — more than one Apple company holds an active link, so
          we cannot tell which company&rsquo;s metrics to read. Ask an engineer to resolve it first.
        </Alert>
      )}

      {loading && (
        <Typography variant="caption" color="secondary">
          Reading the latest figures&hellip;
        </Typography>
      )}

      {!loading && readError && <ReadErrorNotice message={readError} />}

      {!loading && !readError && !projection && (
        <Alert color="info">
          The daily insights job has not written anything for this client yet. That is the expected
          state for an account that has only just been linked — it is NOT a report of zero
          engagement, and there is nothing to press: the job runs on its own.
        </Alert>
      )}

      {!loading && !readError && projection && !bucket && (
        <Alert color="info">
          A record exists for this client but holds no PRODUCTION figures. This panel deliberately
          shows production only — a number read against Apple&rsquo;s test environment is not this
          client&rsquo;s engagement, and rendering one here would be indistinguishable from the
          truth.
        </Alert>
      )}

      {!loading && !readError && bucket && <InsightsBody bucket={bucket} />}
    </div>
  );
}

/**
 * The read itself failed — a different thing from every state the projection can describe.
 *
 * ⚠️ IT NAMES THE MISSING FIRESTORE RULE, because that is the overwhelmingly likely cause and it is a
 * HUMAN action nobody can do from here: rules for this project are console-managed and shared across
 * projects, so there is no `firestore.rules` in this repo and CI deploys none. Until someone adds a
 * read rule for `apple_insights_status`, the collection is default-denied and this panel reads
 * nothing — which must not look like "this client has no engagement".
 */
function ReadErrorNotice({ message }: { message: string }) {
  return (
    <Alert color="error">
      <Typography variant="body2">
        Could not read this client&rsquo;s insights record:{" "}
        <span className="font-mono">{message}</span>. Nothing is wrong with the client&rsquo;s Apple
        presence and no number here is a measurement. If this says permission denied, the Firestore
        read rule for <span className="font-mono">apple_insights_status</span> has not been added
        yet — that is a one-time console change an engineer has to make.
      </Typography>
    </Alert>
  );
}

/** One environment bucket: what the daily job last managed, then the numbers if there are any. */
function InsightsBody({ bucket }: { bucket: InsightsEnvBucket }) {
  const status = insightsStatusOf(bucket);
  const observed = formatInsightsTimestamp(bucket.observedAt);
  const lastRead = formatInsightsTimestamp(bucket.lastReadAt);

  // ⚠️ `NO_REPORTS` AND `READ_FAILED` BOTH RENDER AS AN EMPTY PANEL AND MUST NOT READ THE SAME. The
  // first is Apple having nothing for this client (expected at launch: nothing currently asks Apple to
  // create a report). The second is our own job failing to read Apple.
  if (status === "NO_REPORTS") {
    return (
      <div className="flex flex-col gap-3">
        <Alert color="info">
          Apple has no insights report for this client yet. Nothing in DearDoc currently asks Apple
          to create one, so this is the EXPECTED state at launch rather than a fault — and it is not
          a measurement of zero engagement.
          {/* Corroboration, and only when we actually have it: a count of 0 on Apple's own search
              page is independent evidence for this status. An absent count is not, so no sentence is
              printed rather than one reading "Apple reported — reports". */}
          {typeof bucket.reportsFound === "number"
            ? ` Apple's report list held ${formatMetricValue(bucket.reportsFound)} reports.`
            : ""}
        </Alert>
        <LastCheckedLine observed={observed} lastRead={lastRead} />
      </div>
    );
  }

  if (status === "READ_FAILED") {
    return (
      <div className="flex flex-col gap-3">
        <Alert color="error">
          Our daily check could not read Apple on its last run, so nothing below has been refreshed.
          This is a plumbing problem on our side, NOT a statement about this client&rsquo;s
          engagement — and it is not the same as Apple having no data. If it persists for more than
          a day or two, ask engineering.
        </Alert>
        {/* ⚠️ THE CASE THIS PANEL EXISTS TO RENDER HONESTLY: figures from one day, a failed check on a
            later one. `lastReadAt` moved and `observedAt` did not, so the numbers are real but old,
            and saying only one of those two things would mislead in either direction. */}
        {isShowingStaleFigures(bucket) && observed && lastRead && (
          <Alert color="warning">
            Showing the figures read on {observed}. The most recent check, on {lastRead}, failed —
            so these numbers are real but stale, and anything that happened since is not in them.
          </Alert>
        )}
        {/* The previous successful read's figures, if the bucket still carries any. `hasFigures` is
            what decides — NOT `appointmentTaps !== undefined`, which would hide a bucket whose hero
            number was unreadable while its other metric rows were fine. */}
        {hasFigures(bucket) && <MetricsBlock bucket={bucket} observed={observed} />}
      </div>
    );
  }

  if (status === "REPORT_UNREADABLE") {
    return (
      <div className="flex flex-col gap-3">
        <Alert color="warning">
          Apple returned a report we could not read, so its numbers are UNAVAILABLE rather than
          zero. The report exists at Apple&rsquo;s end; what we could not do is get figures out of
          it. Escalate with the report id below.
        </Alert>
        <ProvenanceRow bucket={bucket} />
        <LastCheckedLine observed={observed} lastRead={lastRead} />
      </div>
    );
  }

  if (status === null) {
    return (
      <div className="flex flex-col gap-3">
        <Alert color="warning">
          {bucket.status
            ? `The daily insights job recorded a status this console does not recognise ("${bucket.status}") — it is newer than this screen. Nothing below is being interpreted for you: read it as raw data and ask engineering.`
            : "The daily insights job left no status on this record, so what it managed on its last run is unknown. Nothing below is being interpreted for you, and none of it should be read as a measurement. Ask engineering."}
        </Alert>
        <ProvenanceRow bucket={bucket} />
        <LastCheckedLine observed={observed} lastRead={lastRead} />
      </div>
    );
  }

  // REPORT_FOUND.
  return (
    <div className="flex flex-col gap-4">
      {/* ⚠️ PENDING IS NOT ZERO, AND THAT IS THE POINT OF SAYING SO. Apple has accepted the report and
          has not finished computing it, so an absent number means "not calculated yet". */}
      {bucket.readiness === "PENDING" && (
        <Alert color="info">
          Apple is still calculating this report, so anything missing below is NOT a zero — it has
          not been computed yet. The daily check will pick up the finished figures on its own.
        </Alert>
      )}
      {bucket.readiness === "FAILED" && (
        <Alert color="error">
          Apple reported that this report FAILED to generate
          {bucket.reportState ? ` (${bucket.reportState})` : ""}, so any numbers below are partial
          at best. Escalate with the report id.
        </Alert>
      )}
      {isShowingStaleFigures(bucket) && observed && lastRead && (
        <Alert color="warning">
          Showing the figures read on {observed}. A later check, on {lastRead}, did not refresh
          them.
        </Alert>
      )}
      <MetricsBlock bucket={bucket} observed={observed} />
      <ProvenanceRow bucket={bucket} />
      <LastCheckedLine observed={observed} lastRead={lastRead} />
    </div>
  );
}

/** The hero tile, the other metric rows, and the "when were these read" line that qualifies them. */
function MetricsBlock({
  bucket,
  observed,
}: {
  bucket: InsightsEnvBucket;
  observed: string | undefined;
}) {
  const others = secondaryMetrics(bucket.metrics);
  const unknown = unknownMetricNames(bucket.metrics);

  return (
    <div className="flex flex-col gap-6">
      <HeroMetric taps={bucket.appointmentTaps ?? null} observed={observed} />

      {others.length > 0 && (
        <div className="flex flex-col gap-2">
          <Typography variant="subtitle2">Other engagement</Typography>
          <div className="flex flex-wrap gap-3">
            {others.map((metric) => (
              <div
                key={metric.name}
                className="flex min-w-[180px] flex-col gap-1 rounded-lg border border-slate-200 p-3 dark:border-slate-700"
              >
                <Typography variant="caption" color="secondary">
                  {metricLabel(metric.name)}
                </Typography>
                <Typography variant="subtitle1">{formatMetricValue(metric.value)}</Typography>
              </div>
            ))}
          </div>
        </div>
      )}

      {unknown.length > 0 && (
        // Not an error — Apple's metric vocabulary is open and the list we know is partial by
        // construction. The rows themselves are rendered above with a humanised label; this line
        // exists so a new metric is NOTICED rather than merely displayed, and so someone can add a
        // friendly label for it.
        <Alert color="info">
          Apple sent {unknown.length} metric {unknown.length === 1 ? "name" : "names"} this console
          does not have a label for yet: {unknown.join(", ")}. Their values are shown above and are
          still correct.
        </Alert>
      )}
    </div>
  );
}

/**
 * THE NUMBER THE WHOLE INTEGRATION IS JUDGED BY — Apple's count of taps on the booking CTA.
 *
 * ⚠️ `null` IS RENDERED DIFFERENTLY FROM `0`, AND THAT DISTINCTION IS THE POINT. "Apple counted 0
 * appointments" is a product problem to act on; "we could not read an appointment count" is a
 * plumbing problem to fix. Both would look identical if this rendered `?? 0`, and a staff member
 * would draw exactly the wrong conclusion — so the absent case says so in words, and the real zero
 * says IT is real.
 *
 * ⚠️ AND THE NUMBER COMES FROM `appointmentTaps`, NOT FROM MATCHING A METRIC NAME. The projection
 * pre-extracts it precisely so no browser code depends on Apple's string staying the same.
 */
function HeroMetric({ taps, observed }: { taps: number | null; observed: string | undefined }) {
  const unavailable = taps === null;
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50 p-6 dark:border-slate-700 dark:bg-slate-800">
      <Typography variant="subtitle2" color="secondary">
        {metricLabel(APPOINTMENT_METRIC_NAME)}
      </Typography>
      {unavailable ? (
        <>
          <Typography variant="h4" color="secondary">
            &mdash;
          </Typography>
          <Typography variant="caption" color="secondary">
            Apple did not include a readable appointment count in this report. This is NOT the same
            as zero appointments — the number is unavailable, not measured.
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="h3">{formatMetricValue(taps)}</Typography>
          <Typography variant="caption" color="secondary">
            {taps === 0
              ? "Apple counted NO taps on this client's booking button. That is a real measurement, not a missing number — this is the metric the ABC integration exists to move."
              : "Taps on this client's booking button, as counted by Apple. This is the metric the ABC integration exists to move."}
          </Typography>
        </>
      )}
      {observed && (
        <Typography variant="caption" color="secondary">
          Read on {observed}. Figures update once a day.
        </Typography>
      )}
    </div>
  );
}

/**
 * Provenance for an escalation: which Apple report, in what state, created and completed when.
 *
 * ⚠️ THE TWO DATES ARE APPLE'S RAW STRINGS and their format is unconfirmed against real Apple, so
 * `formatInsightsTimestamp` shows the raw value when it does not parse. Never "Invalid Date".
 */
function ProvenanceRow({ bucket }: { bucket: InsightsEnvBucket }) {
  const created = formatInsightsTimestamp(bucket.reportCreatedDate);
  const completed = formatInsightsTimestamp(bucket.reportCompletedDate);
  if (!bucket.reportId && !bucket.reportState && !created && !completed) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {bucket.reportId && (
        <Chip size="small" colorScheme="grayLight">
          Report {bucket.reportId}
        </Chip>
      )}
      {bucket.reportState && (
        <Chip size="small" colorScheme="grayLight">
          {bucket.reportState}
        </Chip>
      )}
      {created && (
        <Typography variant="caption" color="secondary">
          Apple created it: {created}
        </Typography>
      )}
      {completed && (
        <Typography variant="caption" color="secondary">
          Apple completed it: {completed}
        </Typography>
      )}
    </div>
  );
}

/**
 * "Observed" versus "last tried", printed together whenever both exist.
 *
 * The two are genuinely different facts and the pair is what makes a stale panel legible: the numbers
 * are as of `observedAt`, and our most recent attempt was `lastReadAt`. On a healthy day they are
 * minutes apart and this line is simply reassurance.
 */
function LastCheckedLine({
  observed,
  lastRead,
}: {
  observed: string | undefined;
  lastRead: string | undefined;
}) {
  if (!observed && !lastRead) return null;
  return (
    <Typography variant="caption" color="secondary">
      {observed ? `Figures read on ${observed}. ` : "No figures have ever been read. "}
      {lastRead ? `Last checked ${lastRead}. ` : ""}
      The backend job checks once a day; this panel never asks Apple itself.
    </Typography>
  );
}
